import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogClose } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ShoppingCart, ShoppingBasket, Flame, Activity, Wheat, Droplet, RefreshCw, ChevronDown, ChevronUp, User, Check, Search, Users, Star, X, Info, UtensilsCrossed } from 'lucide-react';
import Navbar from '@/components/Navbar';
import GroceryListModal from '@/components/GroceryListModal';
import MealPlanSelector from '@/components/MealPlanSelector';
import MealPrepModal from '@/components/MealPrepModal';
import { Checkbox } from '@/components/ui/checkbox';
import ReportRecipeButton from '@/components/ReportRecipeButton';
import InstructionsDisplay from '@/components/InstructionsDisplay';
import { logActivity } from '@/lib/activityLog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to fix image URLs that point to old/different preview domains
const fixImageUrl = (url) => {
  if (!url) return null;
  // Skip data URLs
  if (url.startsWith('data:')) return url;
  // If URL contains ANY emergentagent.com subdomain OR getmealguide domain, normalize to current BACKEND_URL
  // This catches: test-client-view, foodgen-dashboard, client-dashboard-fix, mealguide-ai, mealguide, etc.
  if (url.includes('.emergentagent.com/api/') || url.includes('getmealguide.com/api/')) {
    const pathMatch = url.match(/\/api\/uploads\/.+$/);
    if (pathMatch) {
      return `${BACKEND_URL}${pathMatch[0]}`;
    }
  }
  return url;
};

export default function MealPlanModern({ user, onLogout }) {
  const [mealPlan, setMealPlan] = useState(null);
  const [allPlans, setAllPlans] = useState([]);
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedRecipes, setExpandedRecipes] = useState({});
  const [swapDialogOpen, setSwapDialogOpen] = useState(false);
  const [swappingRecipe, setSwappingRecipe] = useState(null);
  const [swapOptions, setSwapOptions] = useState([]);
  const [loadingSwapOptions, setLoadingSwapOptions] = useState(false);
  const [swapSearchQuery, setSwapSearchQuery] = useState('');
  const [swapDietFilter, setSwapDietFilter] = useState('any'); // Diet type filter (new schema)
  const [swapCookingTimeFilter, setSwapCookingTimeFilter] = useState('any');
  const [swapDifficultyFilter, setSwapDifficultyFilter] = useState('any');
  const [expandedRecipeId, setExpandedRecipeId] = useState(null); // Track which recipe details are expanded
  const [bypassPreferences, setBypassPreferences] = useState(false);
  const [selectedRecipes, setSelectedRecipes] = useState(new Set());
  const [showGroceryList, setShowGroceryList] = useState(false);
  const [mealPrepOpen, setMealPrepOpen] = useState(false);
  const [selectedRecipeDetail, setSelectedRecipeDetail] = useState(null);
  const [recipeDetailOpen, setRecipeDetailOpen] = useState(false);
  const [servings, setServings] = useState(1); // Servings selector (1-7)
  const [loggedRecipes, setLoggedRecipes] = useState(new Set());
  const [todaysCalories, setTodaysCalories] = useState(0);
  const [foodBankItems, setFoodBankItems] = useState([]);
  const [planPreviewOpen, setPlanPreviewOpen] = useState(false);
  const [previewPlan, setPreviewPlan] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [expandedPreviewRecipes, setExpandedPreviewRecipes] = useState({});
  const [hasMultiplePlans, setHasMultiplePlans] = useState(false);
  const [userHasSelectedPlan, setUserHasSelectedPlan] = useState(false);
  const [todaysMealProgress, setTodaysMealProgress] = useState(null);
  const [activeMealTab, setActiveMealTab] = useState(null); // For tabbed meal view
  const [favoriteRecipes, setFavoriteRecipes] = useState(new Set()); // Favorite recipe IDs
  const [loggingStatus, setLoggingStatus] = useState(null); // Today's logging status
  const [userDietType, setUserDietType] = useState('standard'); // User's diet type from onboarding
  const [activeDietTab, setActiveDietTab] = useState('standard'); // Active tab in swap dialog
  const [infoModalOpen, setInfoModalOpen] = useState(false); // Info modal state

  useEffect(() => {
    logActivity('meal_plan_viewed');
    fetchAllPlans();
    fetchTodaysLogs();
    fetchTodaysMealProgress();
    fetchFavorites();
    fetchLoggingStatus();
    fetchUserDietType();
  }, []);

  const fetchUserDietType = async () => {
    try {
      const response = await axios.get(`${API}/onboarding/answers`);
      const dietType = response.data?.diet_type || 'standard';
      setUserDietType(dietType.toLowerCase());
      setActiveDietTab(dietType.toLowerCase());
    } catch (error) {
      console.error('Error fetching diet type:', error);
      setUserDietType('standard');
      setActiveDietTab('standard');
    }
  };

  // All meal boxes start collapsed - user clicks to expand
  const fetchTodaysMealProgress = async () => {
    try {
      const response = await axios.get(`${API}/consistency-streak`);
      setTodaysMealProgress(response.data?.recipe_logging || null);
    } catch (error) {
      console.error('Error fetching meal progress:', error);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorite-recipes`);
      setFavoriteRecipes(new Set(response.data?.favorite_ids || []));
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  const toggleFavorite = async (recipeId, e) => {
    if (e) e.stopPropagation();
    
    const isFavorite = favoriteRecipes.has(recipeId);
    
    // Optimistic update
    const newFavorites = new Set(favoriteRecipes);
    if (isFavorite) {
      newFavorites.delete(recipeId);
    } else {
      newFavorites.add(recipeId);
    }
    setFavoriteRecipes(newFavorites);
    
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorite-recipes/${recipeId}`);
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API}/favorite-recipes/${recipeId}`);
        toast.success('Added to favorites ⭐');
      }
    } catch (error) {
      // Revert on error
      setFavoriteRecipes(favoriteRecipes);
      toast.error('Failed to update favorite');
    }
  };

  const fetchLoggingStatus = async () => {
    try {
      const response = await axios.get(`${API}/recipe-logs/today/status`);
      setLoggingStatus(response.data);
    } catch (error) {
      console.error('Error fetching logging status:', error);
    }
  };

  const fetchAllPlans = async () => {
    try {
      // Fetch all available plans
      const plansResponse = await axios.get(`${API}/meal-plans/me/all`);
      const plans = plansResponse.data || [];
      setAllPlans(plans);
      
      // If no plans at all, stop loading and show "No Meal Plan Yet"
      if (plans.length === 0) {
        setLoading(false);
        return;
      }
      
      // Check if user has multiple plans
      const hasMultiple = plans.length > 1;
      setHasMultiplePlans(hasMultiple);
      
      // Find currently selected plan
      const selectedPlan = plans.find(p => p.currently_selected) || plans[0];
      
      if (selectedPlan) {
        setCurrentPlanId(selectedPlan.id);
        
        // If only 1 plan, fetch it immediately
        // If multiple plans, wait for user to click
        if (!hasMultiple) {
          await fetchMealPlan(selectedPlan.id);
          setUserHasSelectedPlan(true);
        } else {
          setLoading(false);
          setUserHasSelectedPlan(false);
        }
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Failed to load meal plans');
      setLoading(false);
    }
  };

  const fetchMealPlan = async (planId = null) => {
    try {
      const url = planId ? `${API}/meal-plans/me?plan_id=${planId}` : `${API}/meal-plans/me`;
      const response = await axios.get(url);
      setMealPlan(response.data);
      if (response.data) {
        setCurrentPlanId(response.data.id);
      }
    } catch (error) {
      console.error('Error fetching meal plan:', error);
      toast.error('Failed to load meal plan');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (planId) => {
    // Switch to the selected plan and show recipes
    try {
      await axios.post(`${API}/meal-plans/select/${planId}`);
      setCurrentPlanId(planId);
      await fetchMealPlan(planId);
      setUserHasSelectedPlan(true);
      toast.success('Viewing selected meal plan');
    } catch (error) {
      console.error('Error switching plan:', error);
      toast.error('Failed to switch plan');
    }
  };

  const handleSwitchToPlan = async (planId) => {
    try {
      await axios.post(`${API}/meal-plans/select/${planId}`);
      setCurrentPlanId(planId);
      await fetchMealPlan(planId);
      setPlanPreviewOpen(false);
      toast.success('Switched to selected meal plan');
    } catch (error) {
      console.error('Error switching plan:', error);
      toast.error('Failed to switch plan');
    }
  };

  const fetchTodaysLogs = async () => {
    try {
      const response = await axios.get(`${API}/recipe-logs/today`);
      const loggedIds = new Set(response.data.map(log => log.recipe.id));
      setLoggedRecipes(loggedIds);
      
      // Calculate today's calories
      const calories = response.data.reduce((sum, log) => sum + (log.recipe.total_kcal || 0), 0);
      setTodaysCalories(calories);
    } catch (error) {
      console.error('Error fetching logged recipes:', error);
    }
  };

  const handleLogRecipe = async (recipe, e) => {
    if (e) e.stopPropagation();
    
    // Ensure we have a meal_type - fall back to getting it from the recipe's position in the meal plan
    let mealType = recipe.meal_type;
    if (!mealType || mealType === '') {
      // Try to find it from the grouped recipes
      for (const [mt, recipes] of Object.entries(recipesByMealType || {})) {
        if (recipes.some(r => r.id === recipe.id)) {
          mealType = mt;
          break;
        }
      }
    }
    // Final fallback
    if (!mealType || mealType === '') {
      mealType = 'Other';
    }
    
    try {
      const response = await axios.post(`${API}/recipe-logs`, {
        recipe_id: recipe.id,
        meal_plan_id: mealPlan.id,
        meal_type: mealType,
        logged_kcal: recipe.total_kcal  // Send the DISPLAYED calories for accurate tracking
      });
      
      // Refetch from server to ensure accuracy
      await fetchTodaysLogs();
      await fetchTodaysMealProgress();  // Update meal completion status
      await fetchLoggingStatus();  // Update logging status
      
      // Show toast based on action
      if (response.data.action === 'added') {
        toast.success(response.data.message || 'Recipe logged!');
        
        // Check if day is now complete
        const statusResponse = await axios.get(`${API}/recipe-logs/today/status`);
        if (statusResponse.data.is_complete) {
          toast.success('🎉 All meals logged for today! Great job! Resets at 00:00 UTC', { duration: 5000 });
        }
      } else {
        toast.success('Recipe removed from log');
      }
    } catch (error) {
      console.error('Error logging recipe:', error);
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Failed to log recipe');
      } else {
        toast.error('Failed to log recipe');
      }
    }
  };

  const fetchSwapOptions = async (recipe, bypass = false) => {
    setLoadingSwapOptions(true);
    try {
      const response = await axios.get(`${API}/recipes/${recipe.id}/swap-options`, {
        params: { bypass_preferences: bypass }
      });
      setSwapOptions(response.data.compatible_recipes || []);
    } catch (error) {
      console.error('Error fetching swap options:', error);
      toast.error('Failed to load alternatives');
      setSwapOptions([]);
    } finally {
      setLoadingSwapOptions(false);
    }
  };

  const handleSwapClick = async (recipe) => {
    setSwappingRecipe(recipe);
    setSwapDialogOpen(true);
    setSwapSearchQuery(''); // Reset search
    setSwapDietFilter('any'); // Reset diet filter
    setSwapCookingTimeFilter('any'); // Reset cooking time filter
    setSwapDifficultyFilter('any'); // Reset difficulty filter
    setBypassPreferences(false); // Reset bypass toggle
    setActiveDietTab(userDietType); // Reset to user's diet type tab
    
    await fetchSwapOptions(recipe, false);
  };

  const handleToggleBypass = async () => {
    const newBypass = !bypassPreferences;
    setBypassPreferences(newBypass);
    if (swappingRecipe) {
      await fetchSwapOptions(swappingRecipe, newBypass);
    }
  };

  const handleSwap = async (newRecipeId) => {
    try {
      await axios.put(`${API}/recipes/${swappingRecipe.id}/swap`, { new_recipe_id: newRecipeId });
      toast.success('Recipe swapped successfully!');
      logActivity('recipe_swapped');
      setSwapDialogOpen(false);
      fetchMealPlan();
    } catch (error) {
      console.error('Error swapping recipe:', error);
      toast.error('Failed to swap recipe');
    }
  };

  const getFilteredSwapOptions = () => {
    let filtered = [...swapOptions];

    // Apply search filter
    if (swapSearchQuery.trim()) {
      const query = swapSearchQuery.toLowerCase();
      filtered = filtered.filter(recipe => {
        const nameMatch = recipe.name.toLowerCase().includes(query);
        const ingredientMatch = recipe.ingredients?.some(ing => 
          ing.food_item_name?.toLowerCase().includes(query)
        );
        return nameMatch || ingredientMatch;
      });
    }

    // Apply diet type filter (supports both old and new schema)
    if (swapDietFilter && swapDietFilter !== 'any') {
      filtered = filtered.filter(r => {
        const filterLower = swapDietFilter.toLowerCase().replace(/\s+/g, '_');
        
        // NEW SCHEMA: Check diet_type field first
        const recipeDietType = (r.diet_type || '').toLowerCase();
        if (recipeDietType) {
          // For modifiers (high_protein, halal), check boolean flags
          if (filterLower === 'high_protein') {
            return r.high_protein === true;
          }
          if (filterLower === 'halal') {
            return r.halal === true;
          }
          // For diet types, compare directly
          return recipeDietType === filterLower;
        }
        
        // FALLBACK: Old schema with dietary_categories array
        const categories = (r.dietary_categories || []).map(c => c.toLowerCase().replace(/\s+/g, '_'));
        switch(filterLower) {
          case 'standard':
            return categories.includes('standard') || categories.length === 0;
          case 'vegetarian':
            return categories.includes('vegetarian');
          case 'vegan':
            return categories.includes('vegan');
          case 'pescatarian':
            return categories.includes('pescatarian');
          case 'keto':
            return categories.includes('keto');
          case 'high_protein':
            return categories.includes('high_protein') || (r.total_protein || 0) >= 25;
          case 'halal':
            return categories.includes('halal');
          default:
            return true;
        }
      });
    }

    // Apply cooking time filter - extract minutes and compare
    if (swapCookingTimeFilter && swapCookingTimeFilter !== 'any') {
      filtered = filtered.filter(r => {
        const timeStr = (r.estimated_time || r.cooking_time || '').toLowerCase();
        if (!timeStr) return false;
        
        // Extract max minutes from time string
        let maxMins = 30; // default
        if (timeStr.includes('<')) {
          // "< 15 mins" -> 15
          const match = timeStr.match(/(\d+)/);
          if (match) maxMins = parseInt(match[1]);
        } else if (timeStr.includes('-')) {
          // "15-30 mins" -> 30 (take max)
          const parts = timeStr.split('-');
          const match = parts[1]?.match(/(\d+)/);
          if (match) maxMins = parseInt(match[1]);
        } else {
          // "20 mins" -> 20
          const match = timeStr.match(/(\d+)/);
          if (match) maxMins = parseInt(match[1]);
        }
        
        // Filter based on selected time range
        switch(swapCookingTimeFilter) {
          case 'quick': return maxMins <= 15;
          case 'fast': return maxMins <= 20;
          case 'medium': return maxMins <= 35;
          case 'long': return maxMins > 35;
          default: return true;
        }
      });
    }

    // Apply difficulty filter
    if (swapDifficultyFilter && swapDifficultyFilter !== 'any') {
      filtered = filtered.filter(r => {
        const diff = (r.difficulty || '').toLowerCase();
        return diff === swapDifficultyFilter.toLowerCase();
      });
    }

    // Sort favorites first
    filtered.sort((a, b) => {
      const aFav = favoriteRecipes.has(a.id) ? 0 : 1;
      const bFav = favoriteRecipes.has(b.id) ? 0 : 1;
      return aFav - bFav;
    });

    return filtered;
  };

  // Group swap options by diet type for tabbed view
  const groupRecipesByDietType = () => {
    const filtered = getFilteredSwapOptions();
    const grouped = {
      standard: [],
      vegetarian: [],
      vegan: [],
      pescatarian: [],
      keto: []
    };
    
    filtered.forEach(recipe => {
      const dietType = (recipe.diet_type || 'standard').toLowerCase();
      if (grouped[dietType]) {
        grouped[dietType].push(recipe);
      } else {
        // Fallback to standard if unknown diet type
        grouped.standard.push(recipe);
      }
    });
    
    return grouped;
  };

  const toggleRecipeExpand = (recipeId) => {
    setExpandedRecipes(prev => ({
      ...prev,
      [recipeId]: !prev[recipeId]
    }));
  };

  const toggleRecipeSelection = (recipeId) => {
    setSelectedRecipes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(recipeId)) {
        newSet.delete(recipeId);
      } else {
        newSet.add(recipeId);
      }
      return newSet;
    });
  };

  const openRecipeDetail = (recipe) => {
    setSelectedRecipeDetail(recipe);
    setServings(1); // Reset to 1 serving when opening a new recipe
    setRecipeDetailOpen(true);
    logActivity('recipe_detail_viewed');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a61e8] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your meal plan...</p>
          </div>
        </div>
      </div>
    );
  }

  // If no meal plan and no plans available at all, show "no plan" message
  if (!mealPlan && allPlans.length === 0) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar user={user} onLogout={onLogout} />
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <div className="bg-gray-50 rounded-2xl p-12">
            <User className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Meal Plan Yet</h2>
            <p className="text-gray-600 mb-6">Your coach will create a personalized meal plan for you soon.</p>
          </div>
        </div>
      </div>
    );
  }

  // Group recipes by meal type, handling multiple snacks as separate categories
  const recipesByMealType = userHasSelectedPlan ? (mealPlan.recipes || []).reduce((acc, recipe) => {
    let mealType = recipe.meal_type || 'Other';
    
    // For snacks, split into snack_1, snack_2, snack_3 based on meal_number
    if (mealType === 'snack') {
      const mealNumber = recipe.meal_number || 1;
      // Group snacks by their position: 4th meal = snack_1, 5th = snack_2, 6th = snack_3
      if (mealNumber === 4) mealType = 'snack_1';
      else if (mealNumber === 5) mealType = 'snack_2';
      else if (mealNumber === 6) mealType = 'snack_3';
      
      // IMPORTANT: Update the recipe object with the transformed meal_type
      // so logging works correctly
      recipe = { ...recipe, meal_type: mealType };
    }
    
    if (!acc[mealType]) {
      acc[mealType] = [];
    }
    acc[mealType].push(recipe);
    return acc;
  }, {}) : {};
  
  // Calculate meals_per_day and snack count early (before sorting/limiting)
  const mealsPerDay = mealPlan?.meals_per_day || 3;
  const snackCount = Math.max(0, mealsPerDay - 3);
  
  // CRITICAL: Global recipe ID tracking to prevent same recipe appearing in multiple meal slots
  // A recipe can only appear in ONE meal slot (e.g., if it's in breakfast, it can't be in snack_1)
  const globalUsedRecipeIds = new Set();
  
  // Process meal types in order to ensure consistent recipe selection
  const mealTypeOrder = ['breakfast', 'lunch', 'dinner', 'snack_1', 'snack_2', 'snack_3'];
  
  mealTypeOrder.forEach(mealType => {
    if (!recipesByMealType[mealType]) return;
    
    // Filter out recipes that are already used in other meal slots
    const availableRecipes = recipesByMealType[mealType].filter(recipe => 
      !globalUsedRecipeIds.has(recipe.id)
    );
    
    // Remove duplicates by recipe ID within this meal type
    const seenIds = new Set();
    const uniqueById = availableRecipes.filter(recipe => {
      if (seenIds.has(recipe.id)) {
        return false;
      }
      seenIds.add(recipe.id);
      return true;
    });
    
    // Sort favorites first, then by day
    uniqueById.sort((a, b) => {
      const aFav = favoriteRecipes.has(a.id) ? 0 : 1;
      const bFav = favoriteRecipes.has(b.id) ? 0 : 1;
      if (aFav !== bFav) return aFav - bFav;
      return (a.day || 0) - (b.day || 0);
    });
    
    // Remove duplicates by recipe NAME within this meal type (keep first occurrence only)
    const seenNames = new Set();
    const uniqueByName = uniqueById.filter(recipe => {
      const name = recipe.name.toLowerCase().trim();
      if (seenNames.has(name)) {
        return false;
      }
      seenNames.add(name);
      return true;
    });
    
    // Limit to 3 options per meal type
    const selectedRecipes = uniqueByName.slice(0, 3);
    
    // Add selected recipe IDs to global tracker so they can't appear in other meals
    selectedRecipes.forEach(recipe => globalUsedRecipeIds.add(recipe.id));
    
    // Update the meal type with the final selection
    recipesByMealType[mealType] = selectedRecipes;
  });

  // Define meal order with separate snack tabs
  const mealTypeDisplayNames = {
    'breakfast': 'Breakfast',
    'lunch': 'Lunch',
    'dinner': 'Dinner',
    'snack_1': 'Snack 1',
    'snack_2': 'Snack 2',
    'snack_3': 'Snack 3'
  };
  
  // Build meal order based on meals_per_day (separate tab for each snack)
  let uniqueMealOrder = ['breakfast', 'lunch', 'dinner'];
  if (snackCount >= 1) uniqueMealOrder.push('snack_1');
  if (snackCount >= 2) uniqueMealOrder.push('snack_2');
  if (snackCount >= 3) uniqueMealOrder.push('snack_3');
  
  const orderedMealTypes = userHasSelectedPlan ? uniqueMealOrder.filter(type => recipesByMealType[type]?.length > 0) : [];
  
  // Calculate total macros - sum one recipe from each meal type
  const totalMacros = userHasSelectedPlan ? (() => {
    const breakfastRecipe = recipesByMealType['breakfast']?.[0];
    const lunchRecipe = recipesByMealType['lunch']?.[0];
    const dinnerRecipe = recipesByMealType['dinner']?.[0];
    const snack1Recipe = recipesByMealType['snack_1']?.[0];
    const snack2Recipe = recipesByMealType['snack_2']?.[0];
    const snack3Recipe = recipesByMealType['snack_3']?.[0];
    
    let totals = { kcal: 0, protein: 0, carbs: 0, fat: 0 };
    
    // Add all meals that exist
    [breakfastRecipe, lunchRecipe, dinnerRecipe, snack1Recipe, snack2Recipe, snack3Recipe].forEach(recipe => {
      if (recipe) {
        totals.kcal += recipe.total_kcal || 0;
        totals.protein += recipe.total_protein || 0;
        totals.carbs += recipe.total_carbs || 0;
        totals.fat += recipe.total_fat || 0;
      }
    });
    
    return totals;
  })() : { kcal: 0, protein: 0, carbs: 0, fat: 0 };

  return (
    <div className="min-h-screen bg-white">
      <Navbar user={user} onLogout={onLogout} />
      
      {/* Header Section */}
      <div className="bg-gradient-to-r from-[#1a61e8] to-[#4a8ff5] text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold">Your Meal Plan</h1>
                <button
                  onClick={() => setInfoModalOpen(true)}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                  aria-label="Meal plan information"
                >
                  <Info className="h-6 w-6" />
                </button>
              </div>
              {userHasSelectedPlan && (
                <div className="space-y-1">
                  <p className="text-blue-100">
                    Target kcal: ~{Math.round((mealPlan?.target_calories || totalMacros.kcal) / 10) * 10}/day
                  </p>
                  {loggingStatus && (
                    <div className="text-sm">
                      <span className="text-white font-medium">
                        Logged: {Math.round(loggingStatus.logged_calories)} kcal
                      </span>
                      <span className="ml-3 text-blue-100">
                        • {loggingStatus.logged_count}/{loggingStatus.total_meals} recipes logged today
                      </span>
                      {loggingStatus.is_complete && (
                        <span className="ml-3 text-green-300 font-semibold">
                          ✓ Complete! Resets at 00:00 UTC
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            {userHasSelectedPlan && (
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={() => setMealPrepOpen(true)}
                  disabled={selectedRecipes.size === 0}
                  className="bg-white/20 hover:bg-white/30 text-white border border-white/30 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="meal-prep-btn"
                >
                  <UtensilsCrossed className="h-4 w-4 mr-2" />
                  Meal Prep {selectedRecipes.size > 0 && `(${selectedRecipes.size})`}
                </Button>
                <Button
                  onClick={() => setShowGroceryList(true)}
                  disabled={selectedRecipes.size === 0}
                  className="bg-white text-[#1a61e8] hover:bg-blue-50"
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  Shopping List ({selectedRecipes.size})
                </Button>
              </div>
            )}
          </div>

          {/* Meal Plan Selector - Always show if multiple plans */}
          {hasMultiplePlans && (
            <MealPlanSelector 
              plans={allPlans}
              currentPlanId={currentPlanId}
              onSelectPlan={handleSelectPlan}
            />
          )}

          {/* Daily Macros Summary - Only show when user has selected a plan */}
          {userHasSelectedPlan && (
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="flex items-center justify-around">
                <div className="text-center">
                  <Flame className="h-6 w-6 mx-auto mb-1 text-orange-300" />
                  <p className="text-xs text-blue-100 mb-1">Calories</p>
                  <p className="text-xl font-bold">{Math.round(totalMacros.kcal)}</p>
                </div>
                <div className="text-center">
                  <Activity className="h-6 w-6 mx-auto mb-1 text-red-300" />
                  <p className="text-xs text-blue-100 mb-1">Protein</p>
                  <p className="text-xl font-bold">{Math.round(totalMacros.protein)}g</p>
                </div>
                <div className="text-center">
                  <Wheat className="h-6 w-6 mx-auto mb-1 text-yellow-300" />
                  <p className="text-xs text-blue-100 mb-1">Carbs</p>
                  <p className="text-xl font-bold">{Math.round(totalMacros.carbs)}g</p>
                </div>
                <div className="text-center">
                  <Droplet className="h-6 w-6 mx-auto mb-1 text-cyan-300" />
                  <p className="text-xs text-blue-100 mb-1">Fat</p>
                  <p className="text-xl font-bold">{Math.round(totalMacros.fat)}g</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Only show content when user has selected a plan */}
      {userHasSelectedPlan && (
        <>
          {/* Today's Meals Complete Banner - Only show when all meals logged */}
          {todaysMealProgress?.today_complete && userHasSelectedPlan && (
            <div className="max-w-7xl mx-auto px-4 pt-6">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 flex items-center gap-4">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white text-xl">
                  ✓
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-green-800 text-lg">Today&apos;s Meals Complete! 🎉</h3>
                  <p className="text-green-700 text-sm">Great job logging all your meals. Keep up the consistency!</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-600">{todaysMealProgress.current_streak}</p>
                  <p className="text-xs text-green-600">day streak</p>
                </div>
              </div>
            </div>
          )}

          {/* Meal Sections */}
          <div className="max-w-7xl mx-auto px-4 py-6">
            {/* Show instruction if multiple plans but none selected */}
            {hasMultiplePlans && !userHasSelectedPlan && (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Select a Meal Plan to View</h3>
                <p className="text-gray-600">Click &quot;View Plan&quot; on one of the meal plans above to see your recipes</p>
              </div>
            )}
            
            {/* Meal Type Pills - Horizontal scroll on mobile */}
            {userHasSelectedPlan && (
              <>
                <div className="flex gap-2 overflow-x-auto pb-4 mb-6 scrollbar-hide">
                  {orderedMealTypes.map(mealType => {
                    const isSelected = activeMealTab === mealType;
                    const isLogged = todaysMealProgress?.today_logged?.includes(mealType);
                    
                    return (
                      <button
                        key={mealType}
                        onClick={() => setActiveMealTab(isSelected ? null : mealType)}
                        data-testid={`meal-pill-${mealType.toLowerCase().replace(' ', '-')}`}
                        className={`flex-shrink-0 px-5 py-2.5 rounded-full font-medium text-sm transition-all ${
                          isSelected
                            ? 'bg-gray-900 text-white shadow-md'
                            : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {mealType}
                        {isLogged && <span className="ml-1.5 text-green-400">✓</span>}
                      </button>
                    );
                  })}
                </div>

                {/* Recipe Cards Grid - Shows when a meal type is selected */}
                {activeMealTab && recipesByMealType[activeMealTab] && (
                  <div>
                    {/* Instruction text - discrete and subtle */}
                    <p className="text-sm text-gray-500 mb-4 ml-1">Pick 1 meal option</p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid={`recipes-grid-${activeMealTab.toLowerCase().replace(' ', '-')}`}>
                    {recipesByMealType[activeMealTab].map(recipe => (
                      <div
                        key={recipe.id}
                        onClick={() => openRecipeDetail(recipe)}
                        data-testid={`recipe-card-${recipe.id}`}
                        className={`bg-white rounded-2xl shadow-sm cursor-pointer hover:shadow-lg transition-all overflow-hidden ${
                          loggedRecipes.has(recipe.id) 
                            ? 'ring-4 ring-green-400 shadow-green-200' 
                            : favoriteRecipes.has(recipe.id) 
                            ? 'ring-2 ring-yellow-400' 
                            : ''
                        }`}
                      >
                        {/* Recipe Image */}
                        <div className="h-48 relative overflow-hidden bg-gray-100 flex items-center justify-center">
                          {recipe.image_url ? (
                            <img 
                              src={fixImageUrl(recipe.image_url)} 
                              alt={recipe.name}
                              className="w-full h-full object-contain"
                              onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.classList.add('bg-gradient-to-br', 'from-orange-50', 'to-amber-100'); }}
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                              <span className="text-4xl opacity-40">
                                {activeMealTab === 'Breakfast' && '🍳'}
                                {activeMealTab === 'Lunch' && '🥗'}
                                {activeMealTab === 'Dinner' && '🍽️'}
                                {activeMealTab === 'Snack 2' && '🥪'}
                                {activeMealTab === 'Snack' && '🍎'}
                              </span>
                            </div>
                          )}
                          
                          {/* Logged badge - LARGER & MORE PROMINENT */}
                          {loggedRecipes.has(recipe.id) && (
                            <div className="absolute top-2 right-2 bg-green-500 p-2 rounded-full shadow-lg ring-2 ring-white">
                              <Check className="h-5 w-5 text-white stroke-[3]" />
                            </div>
                          )}
                          
                          {/* Favorite badge */}
                          {favoriteRecipes.has(recipe.id) && (
                            <div className="absolute top-2 left-2 bg-yellow-400 p-1.5 rounded-full shadow-lg ring-2 ring-white">
                              <Star className="h-3.5 w-3.5 fill-current text-yellow-900" />
                            </div>
                          )}
                        </div>
                        
                        {/* Recipe Info */}
                        <div className="p-3">
                          <h4 className="font-semibold text-gray-900 text-sm line-clamp-2 leading-snug mb-1.5">
                            {recipe.name}
                          </h4>
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                            <span>{recipe.cooking_time || recipe.estimated_time || '10 min'}</span>
                            <span className="font-medium">{Math.round(recipe.total_kcal || 0)} kcal</span>
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={(e) => handleLogRecipe(recipe, e)}
                              data-testid={`log-btn-${recipe.id}`}
                              disabled={loggingStatus?.is_locked && !loggedRecipes.has(recipe.id)}
                              className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                loggedRecipes.has(recipe.id)
                                  ? 'bg-green-500 text-white hover:bg-green-600 shadow-sm'
                                  : loggingStatus?.is_locked
                                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                              title={loggingStatus?.is_locked && !loggedRecipes.has(recipe.id) ? 'Logging locked until 00:00 UTC' : ''}
                            >
                              {loggedRecipes.has(recipe.id) ? (
                                <span className="flex items-center justify-center gap-1">
                                  <Check className="h-3 w-3" />
                                  Logged
                                </span>
                              ) : loggingStatus?.is_locked ? '🔒 Locked' : 'Log'}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleRecipeSelection(recipe.id);
                              }}
                              data-testid={`shopping-btn-${recipe.id}`}
                              className={`px-2.5 py-1.5 rounded-lg transition-colors ${
                                selectedRecipes.has(recipe.id)
                                  ? 'bg-green-500 text-white'
                                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                              }`}
                              title={selectedRecipes.has(recipe.id) ? 'Remove from list' : 'Add to list'}
                            >
                              <ShoppingBasket className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  </div>
                )}

                {/* Empty state when no meal type selected */}
                {!activeMealTab && (
                  <div className="text-center py-16 bg-gray-50 rounded-2xl">
                    <div className="text-5xl mb-4">🍽️</div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Select a meal type</h3>
                    <p className="text-gray-500">Tap on a meal above to see your recipe options</p>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Swap Dialog */}
      <Dialog open={swapDialogOpen} onOpenChange={setSwapDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Swap {swappingRecipe?.name}</DialogTitle>
            <DialogDescription>
              Choose a compatible alternative from your recipe library
            </DialogDescription>
          </DialogHeader>

          {loadingSwapOptions ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a61e8]"></div>
            </div>
          ) : swapOptions.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No compatible alternatives available</p>
          ) : (
            <div className="space-y-4">
              {/* Results Count */}
              <p className="text-sm text-gray-600">
                {swapOptions.length} {swapOptions.length === 1 ? 'recipe' : 'recipes'} found across all diet types
              </p>

              {/* Diet Type Tabs */}
              <Tabs value={activeDietTab} onValueChange={setActiveDietTab} className="w-full">
                <TabsList className="grid w-full grid-cols-5 mb-4">
                  <TabsTrigger value="standard">
                    Standard ({groupRecipesByDietType().standard.length})
                  </TabsTrigger>
                  <TabsTrigger value="vegetarian">
                    Vegetarian ({groupRecipesByDietType().vegetarian.length})
                  </TabsTrigger>
                  <TabsTrigger value="vegan">
                    Vegan ({groupRecipesByDietType().vegan.length})
                  </TabsTrigger>
                  <TabsTrigger value="pescatarian">
                    Pescatarian ({groupRecipesByDietType().pescatarian.length})
                  </TabsTrigger>
                  <TabsTrigger value="keto">
                    Keto ({groupRecipesByDietType().keto.length})
                  </TabsTrigger>
                </TabsList>

                {/* Tab Content for each diet type */}
                {['standard', 'vegetarian', 'vegan', 'pescatarian', 'keto'].map(dietType => (
                  <TabsContent key={dietType} value={dietType} className="mt-0">
                    {groupRecipesByDietType()[dietType].length === 0 ? (
                      <p className="text-center text-gray-500 py-8">
                        No {dietType} recipes available at this calorie tier
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 gap-4">
                        {groupRecipesByDietType()[dietType].map(option => {
                          const isExpanded = expandedRecipeId === option.id;
                          return (
                            <Card 
                              key={option.id} 
                              className={`hover:shadow-lg transition-shadow ${favoriteRecipes.has(option.id) ? 'ring-2 ring-yellow-400' : ''}`}
                            >
                              <CardContent className="p-4">
                                <div className="flex gap-3">
                                  {/* Recipe Image Thumbnail */}
                                  <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 relative flex items-center justify-center">
                                    {option.image_url ? (
                                      <img 
                                        src={fixImageUrl(option.image_url)} 
                                        alt={option.name}
                                        className="w-full h-full object-contain"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                                        <Flame className="h-8 w-8 text-gray-300" />
                                      </div>
                                    )}
                                    {/* Favorite badge on thumbnail */}
                                    {favoriteRecipes.has(option.id) && (
                                      <div className="absolute top-1 left-1 bg-yellow-400 text-yellow-900 p-1 rounded-full">
                                        <Star className="h-3 w-3 fill-current" />
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Recipe Details */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start gap-2 mb-2">
                                      <h4 className="font-semibold line-clamp-2">{option.name}</h4>
                                      {favoriteRecipes.has(option.id) && (
                                        <Badge className="bg-yellow-400 text-yellow-900 text-xs flex-shrink-0">
                                          <Star className="h-3 w-3 fill-current mr-1" />
                                          Favorite
                                        </Badge>
                                      )}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 mb-3">
                                      <span>{Math.round(option.total_kcal || 0)} kcal</span>
                                      <span>P: {Math.round(option.total_protein || 0)}g</span>
                                      <span>C: {Math.round(option.total_carbs || 0)}g</span>
                                      <span>F: {Math.round(option.total_fat || 0)}g</span>
                                      {(option.estimated_time || option.cooking_time) && (
                                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-300">
                                          ⏱️ {option.estimated_time || option.cooking_time}
                                        </Badge>
                                      )}
                                    </div>
                                    
                                    {/* Action Buttons */}
                                    <div className="flex gap-2">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setExpandedRecipeId(isExpanded ? null : option.id);
                                        }}
                                        className="text-xs px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                                      >
                                        {isExpanded ? '▲ Hide Details' : '▼ View Details'}
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSwap(option.id);
                                        }}
                                        className="text-xs px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
                                      >
                                        Select This Recipe
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Expanded Details */}
                                {isExpanded && (
                                  <div className="mt-4 pt-4 border-t space-y-4">
                                    {/* Ingredients */}
                                    {option.ingredients && option.ingredients.length > 0 && (
                                      <div>
                                        <h5 className="font-semibold text-sm mb-2">🥗 Ingredients ({option.ingredients.length})</h5>
                                        <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-gray-700">
                                          {option.ingredients.map((ing, idx) => (
                                            <li key={idx} className="flex items-start">
                                              <span className="text-gray-400 mr-2">•</span>
                                              <span>{ing.display_text || `${ing.amount} ${ing.name}`}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                    
                                    {/* Instructions */}
                                    {option.instructions && option.instructions.length > 0 && (
                                      <div>
                                        <h5 className="font-semibold text-sm mb-2">📋 Instructions</h5>
                                        <ol className="space-y-2 text-sm text-gray-700">
                                          {option.instructions.map((step, idx) => (
                                            <li key={idx} className="flex items-start">
                                              <span className="font-semibold text-blue-600 mr-2 min-w-[20px]">{idx + 1}.</span>
                                              <span>{step}</span>
                                            </li>
                                          ))}
                                        </ol>
                                      </div>
                                    )}
                                    
                                    {/* Additional Info */}
                                    <div className="flex flex-wrap gap-2">
                                      {option.cuisine && (
                                        <Badge variant="outline" className="text-xs">
                                          🌍 {option.cuisine}
                                        </Badge>
                                      )}
                                      {option.difficulty && (
                                        <Badge variant="outline" className="text-xs">
                                          👨‍🍳 {option.difficulty}
                                        </Badge>
                                      )}
                                      {option.high_protein && (
                                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                                          💪 High Protein
                                        </Badge>
                                      )}
                                      {option.one_pot_meal && (
                                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                                          🍲 One Pot
                                        </Badge>
                                      )}
                                      {option.budget && (
                                        <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">
                                          💰 {option.budget}
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    )}
                  </TabsContent>
                ))}
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Recipe Detail Modal */}
      <Dialog open={recipeDetailOpen} onOpenChange={setRecipeDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] p-0 flex flex-col overflow-hidden" hideCloseButton>
          {selectedRecipeDetail && (
            <>
              {/* Fixed Header - Always visible at top */}
              <div className="bg-white px-4 py-3 sm:px-6 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center justify-between gap-3">
                  <DialogTitle className="text-lg sm:text-xl font-semibold flex-1 line-clamp-2 pr-2">
                    {selectedRecipeDetail.name}
                  </DialogTitle>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <ReportRecipeButton 
                      recipeId={selectedRecipeDetail.recipe_id || selectedRecipeDetail.library_recipe_id} 
                      recipeName={selectedRecipeDetail.name}
                    />
                    <DialogClose asChild>
                      <button
                        className="p-2.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                        aria-label="Close"
                        data-testid="recipe-modal-close-btn"
                      >
                        <X className="h-5 w-5 text-gray-700" />
                      </button>
                    </DialogClose>
                  </div>
                </div>
                <DialogDescription className="sr-only">
                  View recipe details, ingredients, and instructions
                </DialogDescription>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 space-y-4">
                {/* Recipe Image - Compact */}
                <div className="w-full h-48 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                  {selectedRecipeDetail.image_url ? (
                    <img 
                      src={fixImageUrl(selectedRecipeDetail.image_url)} 
                      alt={selectedRecipeDetail.name}
                      className="w-full h-full object-contain"
                      onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.classList.add('bg-gradient-to-br', 'from-orange-50', 'to-amber-100'); }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                      <Flame className="h-12 w-12 text-gray-300" />
                    </div>
                  )}
                </div>

                {/* Macros - 2x2 grid always */}
                {(() => {
                  // IMPORTANT: total_kcal already includes cooking essentials from backend
                  // Don't add CE again - just scale by servings
                  const totalKcal = Math.round((selectedRecipeDetail.total_kcal || 0) * servings);
                  const totalProtein = Math.round((selectedRecipeDetail.total_protein || 0) * servings);
                  const totalCarbs = Math.round((selectedRecipeDetail.total_carbs || 0) * servings);
                  const totalFat = Math.round((selectedRecipeDetail.total_fat || 0) * servings);
                  
                  return (
                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.5rem'}}>
                      <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                        <Flame className="h-5 w-5 text-orange-500 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-gray-900">{totalKcal}</p>
                          <p className="text-xs text-gray-500">kcal</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                        <Activity className="h-5 w-5 text-red-500 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-gray-900">{totalProtein}g</p>
                          <p className="text-xs text-gray-500">protein</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                        <Wheat className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-gray-900">{totalCarbs}g</p>
                          <p className="text-xs text-gray-500">carbs</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                        <Droplet className="h-5 w-5 text-blue-500 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-gray-900">{totalFat}g</p>
                          <p className="text-xs text-gray-500">fat</p>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Servings Selector - Compact */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-[#1a61e8]" />
                    <span className="text-sm text-gray-700">Servings</span>
                  </div>
                  <Select value={servings.toString()} onValueChange={(val) => setServings(parseInt(val))}>
                    <SelectTrigger className="w-28 h-9 text-sm">
                      <SelectValue placeholder="1" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1</SelectItem>
                      <SelectItem value="2">2</SelectItem>
                      <SelectItem value="3">3</SelectItem>
                      <SelectItem value="4">4</SelectItem>
                      <SelectItem value="5">5</SelectItem>
                      <SelectItem value="6">6</SelectItem>
                      <SelectItem value="7">7</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Ingredients - Scaled by servings */}
                <div>
                  <h3 className="font-semibold text-base text-gray-900 mb-2">Main Ingredients</h3>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <ul className="space-y-1.5">
                      {selectedRecipeDetail.ingredients?.filter(ing => {
                        // Filter out cooking essentials from main ingredients
                        const essentialIds = (selectedRecipeDetail.cooking_essentials || []).map(e => e.food_bank_id);
                        const ingId = ing.food_bank_id || ing.id;
                        return !essentialIds.includes(ingId);
                      }).map((ing, idx) => {
                        // Scale ingredient quantities - ALWAYS round to whole numbers for g/G/ml/ML
                        const scaleIngredient = (text, multiplier) => {
                          if (multiplier === 1) return text;
                          
                          // Find all numbers with units FIRST (before any modifications)
                          const unitsPattern = /(\d+\.?\d*)\s*([gGkKmM][gGlL]?)\b/g;
                          const unitsMatches = [...text.matchAll(unitsPattern)];
                          
                          // Create a map of positions that have units (to skip in second pass)
                          const skipRanges = unitsMatches.map(m => ({
                            start: m.index,
                            end: m.index + m[1].length
                          }));
                          
                          // Scale numbers with units
                          let result = text.replace(unitsPattern, (match, num, unit) => {
                            const scaled = parseFloat(num) * multiplier;
                            return `${Math.round(scaled)}${unit.toLowerCase()}`;
                          });
                          
                          // Scale standalone numbers - but skip positions that had units in original text
                          const standalonePattern = /(\d+\.?\d*)/g;
                          result = text.replace(standalonePattern, (match, num, offset) => {
                            // Check if this number is in a skip range
                            const inSkipRange = skipRanges.some(range => 
                              offset >= range.start && offset < range.end
                            );
                            
                            if (inSkipRange) {
                              // This was a number with a unit, already scaled
                              const scaled = parseFloat(num) * multiplier;
                              return Math.round(scaled).toString();
                            } else {
                              // Standalone number (like "2 eggs")
                              const scaled = parseFloat(num) * multiplier;
                              return scaled % 1 === 0 ? scaled.toString() : Math.round(scaled).toString();
                            }
                          });
                          
                          return result;
                        };
                        
                        const baseText = ing.display_text || `${ing.quantity || ing.amount || ''} ${ing.unit || ''} ${ing.ingredient_name || ing.food_item_name || ing.name || ''}`;
                        const displayText = scaleIngredient(baseText, servings);
                        const hasGramsInText = displayText.match(/\d+\.?\d*\s*g\b/i);
                        const scaledGrams = ing.grams ? Math.round(ing.grams * servings) : null;
                        
                        // Hide grams for eggs - check multiple fields and display text
                        const ingredientName = (ing.food_item_name || ing.food_bank_name || ing.ingredient_name || ing.name || '').toLowerCase();
                        const isEgg = ingredientName.includes('egg') || displayText.toLowerCase().includes('egg');
                        const showGrams = scaledGrams && !hasGramsInText && !isEgg;
                        
                        return (
                          <li key={idx} className="flex items-start justify-between text-gray-700">
                            <span className="flex items-start flex-1">
                              <span className="text-[#1a61e8] mr-2">•</span>
                              <span>{displayText}</span>
                            </span>
                            {showGrams && (
                              <span className="text-gray-400 ml-2 flex-shrink-0">({scaledGrams}g)</span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>

                {/* Cooking Essentials - Fixed, never scale */}
                {Array.isArray(selectedRecipeDetail.cooking_essentials) && selectedRecipeDetail.cooking_essentials.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-base text-gray-900 mb-2">Cooking Essentials</h3>
                    <p className="text-xs text-gray-500 mb-2">For cooking and seasoning</p>
                    <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                      <ul className="space-y-1.5">
                        {selectedRecipeDetail.cooking_essentials.map((essential, idx) => {
                          // Find the matching ingredient to get quantity
                          const matchingIng = selectedRecipeDetail.ingredients?.find(
                            ing => (ing.food_bank_id || ing.id) === essential.food_bank_id
                          );
                          
                          const name = essential.food_bank_name || essential.display_text || 'Seasoning';
                          const displayText = matchingIng?.display_text || name;
                          
                          return (
                            <li key={idx} className="flex items-start text-gray-700">
                              <span className="text-amber-600 mr-2">•</span>
                              <span className="italic">{displayText}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Instructions */}
                {selectedRecipeDetail.instructions && (
                  <div>
                    <h3 className="font-semibold text-base text-gray-900 mb-3">Instructions</h3>
                    <InstructionsDisplay instructions={selectedRecipeDetail.instructions} />
                  </div>
                )}
              </div>

              {/* Fixed Footer - Action Buttons */}
              <div className="bg-white px-4 py-3 sm:px-6 border-t border-gray-200 flex-shrink-0 space-y-3">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLogRecipe(selectedRecipeDetail);
                  }}
                  className={`w-full h-12 text-base ${loggedRecipes.has(selectedRecipeDetail.id) ? 'bg-green-600 hover:bg-green-700' : 'bg-[#1a61e8] hover:bg-[#1552c7]'}`}
                >
                  <Check className="h-5 w-5 mr-2" />
                  {loggedRecipes.has(selectedRecipeDetail.id) ? 'Logged for Today ✓' : 'Log This Meal'}
                </Button>
                
                <div className="flex gap-3">
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleRecipeSelection(selectedRecipeDetail.id);
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    {selectedRecipes.has(selectedRecipeDetail.id) ? 'In List' : 'Shopping List'}
                  </Button>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      setRecipeDetailOpen(false);
                      handleSwapClick(selectedRecipeDetail);
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Swap Recipe
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Meal Plan Preview Modal */}
      <Dialog open={planPreviewOpen} onOpenChange={setPlanPreviewOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {previewPlan?.plan_name || 'Meal Plan Preview'}
            </DialogTitle>
            <DialogDescription>
              {previewPlan?.target_calories && (
                <span className="text-lg">
                  Target kcal: ~{Math.round((previewPlan.target_calories || 0) / 10) * 10}/day • {previewPlan?.recipes?.length || 0} recipes
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {loadingPreview ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500">Loading meal plan...</div>
            </div>
          ) : previewPlan?.recipes?.length > 0 ? (
            <div className="space-y-6">
              {/* Group recipes by meal type */}
              {['Breakfast', 'Lunch', 'Dinner', 'Snack', 'Snack 2'].map(mealType => {
                const recipes = previewPlan.recipes.filter(r => r.meal_type === mealType);
                if (recipes.length === 0) return null;
                
                return (
                  <div key={mealType}>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{mealType}</h3>
                    <div className="space-y-4">
                      {recipes.map(recipe => {
                        const isExpanded = expandedPreviewRecipes[recipe.id];
                        return (
                          <Card key={recipe.id} className="overflow-hidden">
                            <CardContent className="p-4">
                              <div 
                                className="flex items-center justify-between cursor-pointer"
                                onClick={() => setExpandedPreviewRecipes(prev => ({
                                  ...prev,
                                  [recipe.id]: !prev[recipe.id]
                                }))}
                              >
                                <div className="flex-1">
                                  <h4 className="font-semibold text-gray-900 mb-2">{recipe.name}</h4>
                                  <div className="flex items-center gap-4 text-sm text-gray-600">
                                    <div className="flex items-center gap-1">
                                      <Flame className="h-4 w-4" />
                                      {Math.round(recipe.total_kcal || 0)} kcal
                                    </div>
                                    <div>{Math.round(recipe.total_protein || 0)}g protein</div>
                                    <div>{Math.round(recipe.total_carbs || 0)}g carbs</div>
                                    <div>{Math.round(recipe.total_fat || 0)}g fat</div>
                                  </div>
                                </div>
                                <Button variant="ghost" size="sm">
                                  {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                                </Button>
                              </div>

                              {isExpanded && (
                                <div className="mt-4 space-y-4">
                                  {/* Ingredients */}
                                  <div>
                                    <h5 className="font-semibold text-gray-900 mb-2">Ingredients</h5>
                                    <div className="bg-gray-50 rounded-lg p-3">
                                      <ul className="space-y-1">
                                        {recipe.ingredients?.map((ing, idx) => {
                                          // Round any decimals in gram/ml values for clean display
                                          let displayText = ing.display_text || `${ing.quantity || ing.amount || ''} ${ing.unit || ''} ${ing.ingredient_name || ing.food_item_name || ing.name || ''}`;
                                          displayText = displayText.replace(/(\d+\.?\d*)\s*([gGmM][lL]?)\b/g, (match, num, unit) => `${Math.round(parseFloat(num))}${unit.toLowerCase()}`);
                                          const hasGramsInText = displayText.match(/\d+\s*g\b/i);
                                          
                                          // Hide grams for eggs - they're displayed as count (e.g., "1 Egg")
                                          const ingredientName = (ing.food_item_name || ing.food_bank_name || ing.ingredient_name || ing.name || '').toLowerCase();
                                          const isEgg = ingredientName.includes('egg');
                                          const showGrams = ing.grams && !hasGramsInText && !isEgg;
                                          
                                          return (
                                            <li key={idx} className="flex items-start justify-between text-sm text-gray-700">
                                              <span className="flex items-start flex-1">
                                                <span className="text-[#1a61e8] mr-2">•</span>
                                                <span>{displayText}</span>
                                              </span>
                                              {showGrams && (
                                                <span className="text-gray-400 ml-2 flex-shrink-0">({Math.round(ing.grams)}g)</span>
                                              )}
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    </div>
                                  </div>

                                  {/* Cooking Essentials - Fixed */}
                                  {Array.isArray(recipe.cooking_essentials) && recipe.cooking_essentials.length > 0 && (
                                    <div>
                                      <h5 className="font-semibold text-gray-900 mb-2">Cooking Essentials</h5>
                                      <p className="text-xs text-gray-500 mb-2">Fixed amounts</p>
                                      <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                                        <ul className="space-y-1">
                                          {recipe.cooking_essentials.map((ing, idx) => {
                                            // Build friendly display: "1 tbsp Honey" or fallback to grams
                                            const name = ing.food_item_name || ing.food_bank_name || '';
                                            const unit = ing.unit || '';
                                            const quantity = ing.quantity || ing.amount || 1;
                                            
                                            let displayText;
                                            if (ing.display_text) {
                                              displayText = ing.display_text;
                                            } else if (unit && unit.toLowerCase() !== 'g' && unit.toLowerCase() !== 'ml') {
                                              displayText = `${quantity} ${unit} ${name}`;
                                            } else {
                                              displayText = `${Math.round(ing.grams)}g ${name}`;
                                            }
                                            
                                            displayText = displayText.replace(/(\d+\.?\d*)\s*([gGmM][lL]?)\b/g, (match, num, u) => `${Math.round(parseFloat(num))}${u.toLowerCase()}`);
                                            return (
                                              <li key={idx} className="flex items-start justify-between text-sm text-gray-700">
                                                <span className="flex items-start flex-1">
                                                  <span className="text-amber-600 mr-2">•</span>
                                                  <span>{displayText}</span>
                                                </span>
                                                <span className="text-gray-400 ml-2 flex-shrink-0">({Math.round(ing.grams)}g)</span>
                                              </li>
                                            );
                                          })}
                                        </ul>
                                      </div>
                                    </div>
                                  )}

                                  {/* Instructions */}
                                  {recipe.instructions && (
                                    <div>
                                      <h5 className="font-semibold text-gray-900 mb-3">Instructions</h5>
                                      <InstructionsDisplay instructions={recipe.instructions} />
                                    </div>
                                  )}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* Switch Plan Button */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setPlanPreviewOpen(false)}>
                  Close
                </Button>
                {previewPlan.id !== currentPlanId && (
                  <Button 
                    onClick={() => handleSwitchToPlan(previewPlan.id)}
                    className="bg-[#1a61e8] hover:bg-[#1a61e8]/90"
                  >
                    Switch to This Plan
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              This meal plan has no recipes yet.
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Grocery List Modal */}
      <GroceryListModal
        open={showGroceryList}
        onOpenChange={setShowGroceryList}
        selectedRecipes={selectedRecipes}
        allRecipes={mealPlan?.recipes || []}
        foodBankItems={foodBankItems}
      />

      <MealPrepModal
        open={mealPrepOpen}
        onClose={() => setMealPrepOpen(false)}
        recipes={(mealPlan?.recipes || []).filter(r => selectedRecipes.has(r.id))}
      />

      {/* Info Modal - HOW TO EDIT: Find this section and change the text below */}
      <Dialog open={infoModalOpen} onOpenChange={setInfoModalOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-2xl">How Your Meal Plan Works</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 text-gray-700 overflow-y-auto pr-2">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">📋 Using Your Meal Plan</h3>
              <ul className="space-y-2 text-sm">
                <li>• <strong>Pick 1 meal option</strong> from each meal type (Breakfast, Lunch, Dinner)</li>
                <li>• Click on any meal type to see all available options.</li>
                <li>• Each recipe shows ingredients, instructions and calories.</li>
              </ul>
            </div>

          <div>
              <h3 className="font-semibold text-gray-900 mb-2">⚖️ Weighing/Measuring food</h3>
              <ul className="space-y-2 text-sm">
                <li>• <strong>You have to weigh and measure ingredients accurately.</strong></li>
                <li>• Use a food scale and measuring cup.</li>
                <li>• Weigh frozen foods after defrosting.</li>
                <li>• Weigh foods in liquid (e.g. canned beans) after draining and rinsing.</li>
                <li>• Use grams or ml when listed.</li>
                <li>• Vegetables don't need strict weighing.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">🔄 Swapping Recipes</h3>
              <ul className="space-y-2 text-sm">
                <li>• Click the <strong>swap icon (↻)</strong> in the bottom-right corner of any recipe card</li>
                <li>• Filter by diet type and cooking time</li>
                <li>• All swaps are pre-matched to keep you on track</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">✓ Logging Your Meals</h3>
              <ul className="space-y-2 text-sm">
                <li>• Tap the <strong>&ldquo;Log&rdquo;</strong> button after eating a meal</li>
                <li>• Logging helps track your consistency</li>
                <li>• You can adjust servings (1-7x) for batch cooking or sharing</li>
                <li>• Logged meals show a green checkmark and once all meals are logged, they are locked until next day. ✓</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">🛒 Shopping List</h3>
              <ul className="space-y-2 text-sm">
                <li>• Tap the <strong>shopping basket icon</strong> on recipes you plan to cook</li>
                <li>• Click &ldquo;Shopping List&rdquo; at the top to see all ingredients</li>
                <li>• You can select how many servings you want to buy in bulk</li>
              </ul>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
              <p className="text-sm text-blue-900">
                <strong>💡 Tip:</strong> Your plan is reviewed weekly and adjusted based on your check-ins and progress. 
                Meal prep in bulk 2-3 times a week. Be consistent, keep logging your meals and submit weekly check-ins for the best results!
              </p>
            </div>
          </div>

          <div className="flex justify-end mt-4 pt-4 border-t border-gray-200">
            <Button onClick={() => setInfoModalOpen(false)} className="bg-[#1a61e8] hover:bg-[#1557d1]">
              Got it!
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
